import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { dbUpdateEntry } from "../../db";
import { appendRemark } from "../../utils/entryUtils";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

export function PartnerBoardedModal({ entry, airline, partnerName, date, onClose, onSave, onSendMessage, appDate }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const isEdit = entry.paxExp != null && entry.paxExp !== "";
  const [count, setCount] = useState(isEdit ? String(entry.paxExp) : "");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });

  const handleSave = async () => {
    const n = parseInt(count, 10);
    const maxPax = Number(entry.ttlPax) || 0;
    if (isNaN(n) || n < 0 || (maxPax > 0 && n > maxPax)) return;
    setSaving(true);
    const boardedText = "✓ " + n + " PAX BOARDED" + (remark.trim() ? " — " + remark.trim() : "");
    const newRemark = appendRemark(entry.remark, boardedText, airline, partnerName);
    const updated = { ...entry, paxExp: String(n), remark: newRemark };
    await dbUpdateEntry(updated, date || "");
    const who = partnerName ? airline + "/" + partnerName : airline;
    const msgBody = "[AUTO] " + who + ": " + n + " PAX boarded on " + entry.outFlt + (entry.dest ? " to " + entry.dest : "") + " (inbound: " + entry.inFlt + ")";
    try {
      await onSendMessage({
        from_airline: airline,
        to_airline: "UA",
        body: msgBody,
        urgent: false,
        read: false,
        date: appDate || date || new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }),
        connection_id: entry.id || null,
        from_scco_name: partnerName || null,
      });
    } catch (e) { console.warn("Auto-message failed:", e); }
    onSave(updated);
    setSaving(false);
    onClose();
  };

  const n = parseInt(count, 10);
  const ttl = Number(entry.ttlPax) || 0;
  const overMax = ttl > 0 && !isNaN(n) && n > ttl;
  const valid = !isNaN(n) && n >= 0 && !overMax;

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200,
      display: "flex", alignItems: isWide ? "center" : "flex-end",
      justifyContent: "center", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: isWide ? 400 : "100%",
        borderTop: isWide ? undefined : `2px solid ${tokens.success}`,
        borderRadius: isWide ? 16 : "16px 16px 0 0",
        maxHeight: "88vh", display: "flex", flexDirection: "column", ...dragStyle }}>

        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.18em",
              textTransform: "uppercase", marginBottom: 4 }}>
              {isEdit ? "Edit Boarded Count" : "Report Passengers Boarded"}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              {entry.inFlt} {"->"} {entry.outFlt}
              {entry.dest ? <span style={{ color: SA.silver }}> {"\u00b7"} {entry.dest}</span> : null}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16, flexShrink: 0 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ background: "rgba(110,196,114,0.08)", border: "1px solid rgba(110,196,114,0.25)",
            borderRadius: RADIUS.md, padding: "10px 14px", marginBottom: 16,
            fontSize: TYPOGRAPHY.body, color: tokens.success, lineHeight: 1.5 }}>
            Enter the number of customers from this connection who successfully boarded your outbound flight.
            If no passengers boarded, enter 0. UA SCCO will be notified automatically.
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Customers Boarded
            </label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={count}
              onChange={e => setCount(e.target.value)}
              placeholder="e.g. 18"
              autoFocus
              onKeyDown={e => e.key === "Enter" && valid && handleSave()}
              style={{ width: "100%", boxSizing: "border-box",
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, color: SA.white,
                padding: "14px 16px", fontSize: 22, fontWeight: 700,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none",
                letterSpacing: "0.06em", textAlign: "center" }}
            />
            {entry.ttlPax && Number(entry.ttlPax) > 0 && (
              <div style={{ fontSize: TYPOGRAPHY.caption, color: overMax ? tokens.danger : SA.silver, marginTop: 6, textAlign: "center", transition: "color 0.2s" }}>
                {overMax
                  ? `Cannot exceed total PAX at risk (${ttl})`
                  : `${ttl} total passengers at risk on this connection`}
              </div>
            )}
          </div>
          <div>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Remark <span style={{ color: "#555E6E", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
            </label>
            <textarea
              value={remark}
              onChange={e => setRemark(e.target.value)}
              placeholder="Add a note to UA SCCO..."
              rows={2}
              style={{ width: "100%", boxSizing: "border-box",
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, color: SA.white,
                padding: "12px 14px", fontSize: TYPOGRAPHY.note,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                outline: "none", resize: "none", lineHeight: 1.5 }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="secondary" onClick={onClose}
              style={{ flex: 1, padding: "13px", fontSize: TYPOGRAPHY.caption,
                letterSpacing: "0.12em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                borderRadius: RADIUS.md }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!valid || saving}
              style={{ flex: 2, padding: "13px",
                background: valid && !saving ? "rgba(110,196,114,0.15)" : SA.darkGray,
                border: valid && !saving ? "1px solid rgba(110,196,114,0.5)" : `1px solid ${SA.borderGray}`,
                color: valid && !saving ? tokens.success : SA.silver,
                fontSize: TYPOGRAPHY.body, letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700,
                borderRadius: RADIUS.md, transition: "all 0.2s" }}>
              {saving ? "Saving..." : isEdit ? "Update Boarded" : "Confirm Boarded"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
